import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CalendarDays,
  CalendarPlus,
  Loader2,
  Search,
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  X,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  Repeat,
} from "lucide-react";
import {
  fetchAdminEvents,
  createAdminEvent,
  updateAdminEvent,
  deleteAdminEvent,
  duplicateAdminEvent,
  uploadAdminEventPhoto,
  EVENT_CATEGORY_OPTIONS,
} from "@/api/adminEvents";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const emptyForm = {
  title: "",
  title_de: "",
  title_en: "",
  date: "",
  end_date: "",
  recurring: "",
  recurring_de: "",
  recurring_en: "",
  location: "",
  location_de: "",
  location_en: "",
  category: "",
  description: "",
  description_de: "",
  description_en: "",
  image: "",
  link: "",
  published: true,
};

const Field = ({ label, children, hint, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="text-xs font-[Poppins] font-semibold uppercase tracking-wide text-[#5A7594] block mb-1.5">
      {label}
    </span>
    {children}
    {hint && <span className="text-xs text-[#8AA3B8] block mt-1">{hint}</span>}
  </label>
);

const Toast = ({ toast }) =>
  toast ? (
    <div
      data-testid="admin-events-toast"
      className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-xl shadow-lg font-[Poppins] font-semibold text-sm flex items-center gap-2 ${
        toast.type === "error" ? "bg-[#8b1420] text-white" : "bg-[#173A5E] text-white"
      }`}
    >
      {toast.type === "error" ? <AlertCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
      {toast.message}
    </div>
  ) : null;

const preview = (v) => {
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v;
  if (v.startsWith("/api/")) return `${BACKEND_URL}${v}`;
  return v;
};

const EventFormModal = ({ initial, onClose, onSaved, showToast }) => {
  const isNew = !initial;
  const [form, setForm] = useState(() => {
    const base = { ...emptyForm, ...(initial || {}) };
    // Zorg dat null values niet crashen
    base.recurring = base.recurring || "";
    return base;
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      showToast("Titel is verplicht", "error");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, recurring: form.recurring || null };
      if (isNew) {
        const created = await createAdminEvent(payload);
        showToast(`"${created.title}" aangemaakt`);
        onSaved(created);
      } else {
        const res = await updateAdminEvent(initial.id, payload);
        showToast(`"${res.event.title}" bijgewerkt`);
        onSaved(res.event);
      }
    } catch (err) {
      showToast(err?.response?.data?.detail || "Opslaan mislukt", "error");
    } finally {
      setSaving(false);
    }
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (isNew) {
      showToast("Sla eerst het evenement op, upload dan de foto.", "error");
      return;
    }
    setUploading(true);
    try {
      const res = await uploadAdminEventPhoto(initial.id, file);
      set("image", res.image);
      showToast("Foto geüpload");
    } catch (err) {
      showToast(err?.response?.data?.detail || "Upload mislukt", "error");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const img = preview(form.image);

  return (
    <div
      data-testid="event-form-modal"
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
    >
      <form
        onSubmit={onSubmit}
        className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl my-8"
      >
        <div className="sticky top-0 bg-white border-b border-[#E4EEF5] px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-xl font-bold text-[#173A5E] font-[Poppins]">
            {isNew ? "Nieuw evenement" : `Bewerken: ${initial.title}`}
          </h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="event-form-close"
            className="p-2 rounded-lg hover:bg-[#F4F8FB]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="md:col-span-2">
            <Field label="Titel (NL) *">
              <input
                type="text"
                required
                data-testid="event-form-title"
                value={form.title}
                onChange={(e) => set("title", e.target.value)}
                className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2 focus:border-[#6BC0E8] outline-none"
              />
            </Field>
          </div>
          <Field label="Titel (DE)">
            <input
              type="text"
              value={form.title_de}
              onChange={(e) => set("title_de", e.target.value)}
              className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2"
            />
          </Field>
          <Field label="Titel (EN)">
            <input
              type="text"
              value={form.title_en}
              onChange={(e) => set("title_en", e.target.value)}
              className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2"
            />
          </Field>
          <Field label="Startdatum" hint="ISO YYYY-MM-DD. Laat leeg bij terugkerende events zonder specifieke datum.">
            <input
              type="date"
              data-testid="event-form-date"
              value={form.date}
              onChange={(e) => set("date", e.target.value)}
              className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2"
            />
          </Field>
          <Field label="Einddatum" hint="Alleen invullen als het event meerdere dagen duurt.">
            <input
              type="date"
              value={form.end_date}
              onChange={(e) => set("end_date", e.target.value)}
              className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2"
            />
          </Field>
          <div className="md:col-span-2">
            <Field label="Terugkerend (bv. 'elke vrijdag', 'jaarlijks (juli)')">
              <input
                type="text"
                data-testid="event-form-recurring"
                value={form.recurring}
                onChange={(e) => set("recurring", e.target.value)}
                placeholder="Laat leeg voor eenmalig event"
                className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2"
              />
            </Field>
          </div>
          <Field label="Locatie (NL)">
            <input
              type="text"
              data-testid="event-form-location"
              value={form.location}
              onChange={(e) => set("location", e.target.value)}
              className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2"
            />
          </Field>
          <Field label="Categorie">
            <select
              data-testid="event-form-category"
              value={form.category}
              onChange={(e) => set("category", e.target.value)}
              className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2 bg-white"
            >
              {EVENT_CATEGORY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          <div className="md:col-span-2">
            <Field label="Omschrijving (NL)">
              <textarea
                data-testid="event-form-description"
                rows={4}
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2 font-[Inter]"
              />
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Link (website / meer info)">
              <input
                type="url"
                data-testid="event-form-link"
                value={form.link}
                onChange={(e) => set("link", e.target.value)}
                placeholder="https://…"
                className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2"
              />
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Foto" hint={isNew ? "Sla eerst op, upload daarna." : "JPG/PNG/WEBP — vervangt bestaande."}>
              <div className="flex items-start gap-4 flex-wrap">
                {img && (
                  <img
                    src={img}
                    alt="preview"
                    data-testid="event-form-image-preview"
                    className="w-40 h-28 object-cover rounded-lg border border-[#E4EEF5]"
                  />
                )}
                <div className="flex flex-col gap-2">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    onChange={onPhoto}
                    disabled={isNew || uploading}
                    data-testid="event-form-image-file"
                    className="text-sm"
                  />
                  <input
                    type="text"
                    value={form.image}
                    onChange={(e) => set("image", e.target.value)}
                    placeholder="of /media/… of https://…"
                    className="w-72 max-w-full border border-[#E4EEF5] rounded-lg px-3 py-2 text-xs"
                  />
                  {uploading && <span className="text-xs text-[#2E9CD6]">Bezig met uploaden…</span>}
                </div>
              </div>
            </Field>
          </div>
          <div className="md:col-span-2 pt-2 border-t border-[#E4EEF5]">
            <label className="inline-flex items-center gap-2 text-sm text-[#173A5E] font-[Poppins] cursor-pointer">
              <input
                type="checkbox"
                data-testid="event-form-published"
                checked={form.published}
                onChange={(e) => set("published", e.target.checked)}
                className="w-4 h-4 accent-[#2E9CD6]"
              />
              Zichtbaar op de site (agenda)
            </label>
          </div>

          <details className="md:col-span-2 border border-[#E4EEF5] rounded-lg p-3 bg-[#F9FBFD]">
            <summary className="cursor-pointer font-[Poppins] font-semibold text-sm text-[#173A5E]">
              Vertalingen (DE / EN) — optioneel
            </summary>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              <Field label="Locatie (DE)">
                <input type="text" value={form.location_de} onChange={(e) => set("location_de", e.target.value)} className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2" />
              </Field>
              <Field label="Locatie (EN)">
                <input type="text" value={form.location_en} onChange={(e) => set("location_en", e.target.value)} className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2" />
              </Field>
              <Field label="Terugkerend (DE)">
                <input type="text" value={form.recurring_de} onChange={(e) => set("recurring_de", e.target.value)} className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2" />
              </Field>
              <Field label="Terugkerend (EN)">
                <input type="text" value={form.recurring_en} onChange={(e) => set("recurring_en", e.target.value)} className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2" />
              </Field>
              <Field label="Omschrijving (DE)" className="md:col-span-2">
                <textarea rows={3} value={form.description_de} onChange={(e) => set("description_de", e.target.value)} className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2" />
              </Field>
              <Field label="Omschrijving (EN)" className="md:col-span-2">
                <textarea rows={3} value={form.description_en} onChange={(e) => set("description_en", e.target.value)} className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2" />
              </Field>
            </div>
          </details>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-[#E4EEF5] px-6 py-4 flex items-center justify-end gap-3 rounded-b-2xl">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-[#173A5E] hover:bg-[#F4F8FB] font-[Poppins] font-semibold text-sm">
            Annuleren
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="event-form-save"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#173A5E] hover:bg-[#0F2A46] text-white font-[Poppins] font-semibold text-sm disabled:opacity-60"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {isNew ? "Aanmaken" : "Opslaan"}
          </button>
        </div>
      </form>
    </div>
  );
};

// Hulpfunctie: zelfde dag, een jaar later (voor "opnieuw plannen voor volgend jaar").
const plusYear = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
};

const ReplanModal = ({ item, onClose, onDone, showToast }) => {
  const [date, setDate] = useState(() => plusYear(item.date));
  const [endDate, setEndDate] = useState(() => plusYear(item.end_date));
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!date) {
      showToast("Kies een nieuwe startdatum", "error");
      return;
    }
    setSaving(true);
    try {
      const created = await duplicateAdminEvent(item.id, { date, end_date: endDate });
      showToast(`"${created.title}" opnieuw gepland op ${created.date}`);
      onDone(created);
    } catch (err) {
      showToast(err?.response?.data?.detail || "Opnieuw plannen mislukt", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="event-replan-modal"
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <form
        onSubmit={onSubmit}
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
      >
        <h3 className="text-xl font-bold text-[#173A5E] font-[Poppins] mb-2">
          Opnieuw plannen voor volgend jaar
        </h3>
        <p className="text-sm text-[#5A7594] mb-4">
          Er wordt een <strong>kopie</strong> van <strong>{item.title}</strong> gemaakt met de
          nieuwe datum. De kopie start als <strong>concept</strong> (nog niet publiek) onder
          "Aankomend" — publiceer hem zelf als hij klaar is. Het origineel blijft in het archief staan.
        </p>
        <div className="space-y-4">
          <Field label="Nieuwe startdatum *">
            <input
              type="date"
              required
              data-testid="event-replan-date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2 focus:border-[#6BC0E8] outline-none"
            />
          </Field>
          <Field label="Nieuwe einddatum" hint="Alleen invullen als het event meerdere dagen duurt.">
            <input
              type="date"
              data-testid="event-replan-end-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2 focus:border-[#6BC0E8] outline-none"
            />
          </Field>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-[#173A5E] hover:bg-[#F4F8FB] font-[Poppins] font-semibold text-sm"
          >
            Annuleren
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="event-replan-confirm"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#173A5E] hover:bg-[#0F2A46] text-white font-[Poppins] font-semibold text-sm disabled:opacity-60"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            <CalendarPlus className="w-4 h-4" />
            Kopie plannen
          </button>
        </div>
      </form>
    </div>
  );
};

export const AdminEventsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [pubFilter, setPubFilter] = useState("");
  const [view, setView] = useState("upcoming"); // "upcoming" (Aankomend) | "past" (Voorbij/archief)
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [replan, setReplan] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, type = "ok") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2500);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { view };
      if (q.trim()) params.q = q.trim();
      if (pubFilter === "published") params.published = true;
      if (pubFilter === "hidden") params.published = false;
      const data = await fetchAdminEvents(params);
      setItems(data.items || []);
    } catch (e) {
      showToast(e?.response?.data?.detail || "Ophalen mislukt", "error");
    } finally {
      setLoading(false);
    }
  }, [q, pubFilter, view, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const togglePublished = async (item) => {
    try {
      const res = await updateAdminEvent(item.id, { ...item, recurring: item.recurring || "", published: !item.published });
      setItems((prev) => prev.map((p) => (p.id === item.id ? res.event : p)));
      showToast(res.event.published ? "Zichtbaar" : "Verborgen");
    } catch (e) {
      showToast(e?.response?.data?.detail || "Wijzigen mislukt", "error");
    }
  };

  const doDelete = async (item) => {
    try {
      await deleteAdminEvent(item.id);
      setItems((prev) => prev.filter((p) => p.id !== item.id));
      showToast(`"${item.title}" verwijderd`);
    } catch (e) {
      showToast(e?.response?.data?.detail || "Verwijderen mislukt", "error");
    } finally {
      setConfirmDelete(null);
    }
  };

  const stats = useMemo(() => {
    let pub = 0, hid = 0, adm = 0;
    for (const it of items) {
      if (it.published !== false) pub++; else hid++;
      if (it.admin_modified) adm++;
    }
    return { pub, hid, adm };
  }, [items]);

  return (
    <div data-testid="admin-events-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#173A5E] font-[Poppins] flex items-center gap-2">
            <CalendarDays className="w-7 h-7 text-[#2E9CD6]" />
            Agenda
          </h1>
          <p className="text-sm text-[#5A7594] mt-1">
            Beheer alle evenementen. {stats.pub} zichtbaar · {stats.hid} verborgen · {stats.adm} handmatig aangepast.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          data-testid="btn-new-event"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#173A5E] hover:bg-[#0F2A46] text-white font-[Poppins] font-semibold text-sm shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Nieuw evenement
        </button>
      </div>

      {/* Tabs: Aankomend / Voorbij (archief) — puur datum-gedreven */}
      <div data-testid="admin-events-tabs" className="flex gap-2 mb-4">
        {[
          { key: "upcoming", label: "Aankomend" },
          { key: "past", label: "Voorbij (archief)" },
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setView(tab.key)}
            data-testid={`events-tab-${tab.key}`}
            className={`px-4 py-2 rounded-full font-[Poppins] font-semibold text-sm transition ${
              view === tab.key
                ? "bg-[#173A5E] text-white shadow-sm"
                : "bg-white text-[#173A5E] border border-[#E4EEF5] hover:bg-[#F4F8FB]"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-[#E4EEF5] p-4 mb-4 flex flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[200px] max-w-md bg-[#F4F8FB] rounded-lg px-3 py-2 border border-transparent focus-within:border-[#6BC0E8]">
          <Search className="w-4 h-4 text-[#2E9CD6]" />
          <input
            data-testid="admin-events-search"
            type="search"
            placeholder="Zoek op titel…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="flex-1 bg-transparent outline-none text-sm text-[#173A5E]"
          />
        </div>
        <select
          data-testid="admin-events-filter-status"
          value={pubFilter}
          onChange={(e) => setPubFilter(e.target.value)}
          className="border border-[#E4EEF5] rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">Alle statussen</option>
          <option value="published">Alleen zichtbaar</option>
          <option value="hidden">Alleen verborgen</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-[#2E9CD6]" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-[#5A7594] py-16 text-center">Geen evenementen gevonden.</p>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E4EEF5] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F8FB] text-[#173A5E] font-[Poppins]">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Titel</th>
                  <th className="text-left px-4 py-3 font-semibold">Datum</th>
                  <th className="text-left px-4 py-3 font-semibold">Locatie</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-right px-4 py-3 font-semibold">Acties</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E4EEF5] font-[Inter]">
                {items.map((it) => (
                  <tr key={it.id} data-testid={`event-row-${it.slug}`} className="hover:bg-[#F9FBFD]">
                    <td className="px-4 py-3">
                      <div className="font-[Poppins] font-semibold text-[#173A5E] flex items-center gap-2">
                        {it.title}
                        {it.recurring && (
                          <span title={`Terugkerend: ${it.recurring}`}>
                            <Repeat className="w-3.5 h-3.5 text-[#2E9CD6]" />
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[#8AA3B8]">
                        {it.slug}
                        {it.admin_modified && (
                          <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-[#F4E9D8] text-[#8B6914] text-[10px] font-semibold">
                            handmatig
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[#3E5871] whitespace-nowrap">
                      {it.date || <span className="italic text-[#8AA3B8]">—</span>}
                      {it.end_date && it.end_date !== it.date && <> → {it.end_date}</>}
                      {it.recurring && !it.date && (
                        <div className="text-xs text-[#5A7594] italic">{it.recurring}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#3E5871]">{it.location || "—"}</td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => togglePublished(it)}
                        data-testid={`event-toggle-${it.slug}`}
                        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold ${
                          it.published !== false ? "bg-[#E6F5EA] text-[#2A7A3F]" : "bg-[#FDECEE] text-[#8b1420]"
                        }`}
                      >
                        {it.published !== false ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {it.published !== false ? "Zichtbaar" : "Verborgen"}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {view === "past" && (
                          <button
                            type="button"
                            onClick={() => setReplan(it)}
                            data-testid={`event-replan-${it.slug}`}
                            title="Opnieuw plannen voor volgend jaar"
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[#2A7A3F] hover:bg-[#E6F5EA] font-[Poppins] font-semibold text-xs"
                          >
                            <CalendarPlus className="w-4 h-4" />
                            Opnieuw plannen
                          </button>
                        )}
                        <a
                          href={`/agenda#${it.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`event-view-${it.slug}`}
                          title="Bekijk in agenda"
                          className="p-2 rounded-lg text-[#2E9CD6] hover:bg-[#F4F8FB]"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        <button
                          type="button"
                          onClick={() => setEditing(it)}
                          data-testid={`event-edit-${it.slug}`}
                          className="p-2 rounded-lg text-[#173A5E] hover:bg-[#F4F8FB]"
                          title="Bewerken"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDelete(it)}
                          data-testid={`event-delete-${it.slug}`}
                          className="p-2 rounded-lg text-[#8b1420] hover:bg-[#FDECEE]"
                          title="Verwijderen"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(creating || editing) && (
        <EventFormModal
          initial={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            load();
          }}
          showToast={showToast}
        />
      )}

      {replan && (
        <ReplanModal
          item={replan}
          onClose={() => setReplan(null)}
          onDone={() => {
            setReplan(null);
            setView("upcoming"); // de kopie staat onder "Aankomend"
          }}
          showToast={showToast}
        />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" data-testid="event-confirm-delete">
            <h3 className="text-xl font-bold text-[#173A5E] font-[Poppins] mb-2">Verwijderen bevestigen</h3>
            <p className="text-sm text-[#5A7594] mb-4">
              Weet je zeker dat je <strong>{confirmDelete.title}</strong> wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
              <span className="block mt-2 text-xs">
                Het event komt ook na toekomstige deploys niet meer terug (het wordt op een definitieve verwijderlijst gezet).
              </span>
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 rounded-lg text-[#173A5E] hover:bg-[#F4F8FB] font-[Poppins] font-semibold text-sm"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={() => doDelete(confirmDelete)}
                data-testid="event-confirm-delete-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#8b1420] hover:bg-[#6a0f18] text-white font-[Poppins] font-semibold text-sm"
              >
                <Trash2 className="w-4 h-4" />
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
};

export default AdminEventsPage;
